import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  type DefaultView,
  SavedViewsList,
} from "@/components/news-feed/saved-views-selector/SavedViewsList";
import {
  SearchField,
  SearchFieldClear,
  SearchFieldInput,
} from "@/components/ui/controls/searchfield";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import type { SavedViewItem } from "@/data-access/saved-views/queries/saved-views-queries";
import {
  getKeyMessageFilterExclusionFromFlatSavedView,
  type SavedViewFilter,
  savedViewFilterToSearchParams,
} from "@/lib/utils/filters";
import { addCommasToNumbers } from "@/lib/utils/prettyName";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";

const DEFAULT_FILTER_DAYS = 30;

const NarrativeClustersTabContentManagementPanel = () => {
  const { feedId } = useParams({ strict: false });
  const navigate = useNavigate();

  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalNarrativeClustersCount,
  );
  const isFetchingNarrativeClusters = useDiscoveryContentManagementStore(
    (state) => state.isFetchingNarrativeClustersTableData,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.narrativeClustersSearchTerm,
  );
  const setSearchTerm = useDiscoveryContentManagementStore(
    (state) => state.setNarrativeClustersSearchTerm,
  );
  const updateKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.updateSelectedKeyMessageFilterExclusion,
  );

  const handleViewSelect = (view: SavedViewItem | DefaultView) => {
    if (view.id === null) {
      updateKeyMessageFilterExclusion("OR");
      navigate({
        to: "/discovery/$feedId/clusters",
        params: { feedId: feedId ?? "" },
        search: {
          filterDays: DEFAULT_FILTER_DAYS,
          period: "day",
          selected: { CATEGORY: ["NEWS"] },
          savedViewId: undefined,
        },
      });
    } else if ("feedArticleFilter" in view) {
      updateKeyMessageFilterExclusion(
        getKeyMessageFilterExclusionFromFlatSavedView(view.feedArticleFilter),
      );
      const searchParams = savedViewFilterToSearchParams(
        view.feedArticleFilter as SavedViewFilter,
      );
      navigate({
        to: "/discovery/$feedId/clusters",
        params: { feedId: feedId ?? "" },
        search: {
          filterDays: searchParams.filterDays ?? DEFAULT_FILTER_DAYS,
          startDate: searchParams.startDate,
          endDate: searchParams.endDate,
          period: "day",
          selected: searchParams.selected,
          savedViewId: view.id,
        },
      });
    }
  };

  return (
    <div className="grid grid-cols-2 grid-rows-[minmax(0,1fr)_auto] gap-2 pb-1 pt-6">
      <div className="min-h-0 overflow-auto">
        <SavedViewsList onViewSelect={handleViewSelect} />
      </div>
      <div className="min-h-0 flex items-center justify-end">
        <SearchField
          aria-label="Search narrative clusters"
          className="border rounded-md px-2 py-0.5 w-full max-w-xs"
        >
          <MagnifyingGlassIcon
            aria-hidden
            size={14}
            className="text-slate-300 shrink-0"
          />
          <SearchFieldInput
            placeholder="Search narrative clusters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <SearchFieldClear onPress={() => setSearchTerm("")}>
              <XIcon aria-hidden className="text-gray-400" size={14} />
            </SearchFieldClear>
          )}
        </SearchField>
      </div>
      <div className="col-span-2 text-xs text-gray-900 px-0 pt-7">
        {isFetchingNarrativeClusters ? (
          <Skeleton variant="text" className="h-4 w-16" />
        ) : (
          <>
            {addCommasToNumbers(totalCount)}{" "}
            {totalCount === 1 ? "Narrative Cluster" : "Narrative Clusters"}
          </>
        )}
      </div>
    </div>
  );
};

export default NarrativeClustersTabContentManagementPanel;
